import { PolfEventHoleCompetitionTypeEnum } from '@polf/shared/codegen/types'
import { formatChips } from '@polf/shared/locale'
import { Link } from 'react-router-dom'

import { SIDEGAME_BRAND } from '../../../util/sidegame-brand'

type Comp = {
  id: string
  type: PolfEventHoleCompetitionTypeEnum
  chipValue: number
}

/**
 * Single side-game banner. Brand-coloured per comp type (gold/green/blue).
 * Measured comps (closest/longest) deep-link to `/sidegames#comp-{id}` so
 * the captain lands on the relevant entry without scrolling the list.
 * Beat-the-PRO is informational only — it has no card on /sidegames (bets
 * are placed with the pro at the hole, results live on the /b/ view), so
 * the banner doesn't navigate.
 */
const HoleCompBanner = ({
  playerKey,
  comp,
  showChips,
}: {
  playerKey: string
  comp: Comp
  showChips: boolean
}) => {
  const brand = SIDEGAME_BRAND[comp.type]
  const { Icon } = brand
  // Beat-the-PRO has no fixed chip prize — its economics are the bet payout
  // (win = stake×2, lose = −stake), so `chipValue` is 0 and a "Premie: 0 chips"
  // line is simply wrong. Show the prize only for the measured comps that
  // actually carry one.
  const isBtp = comp.type === PolfEventHoleCompetitionTypeEnum.Beatthepro
  const showPrize = showChips && !isBtp && comp.chipValue > 0
  const inner = (
    <>
      <span
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(circle at 100% 0%, rgba(255, 255, 255, 0.55), transparent 60%)',
        }}
      />
      <span
        aria-hidden
        className={`relative w-12 h-12 rounded-xl flex items-center justify-center shrink-0 shadow ${brand.tileBg}`}
      >
        <Icon size={24} />
      </span>
      <div className="relative flex-1 min-w-0">
        <div className="font-display font-extrabold text-base text-ink-900 truncate leading-tight">
          Hullkonkurranse: {brand.label}
        </div>
        {brand.helpText ? (
          <div className="text-[11px] text-ink-700 italic mt-0.5">
            {brand.helpText}
          </div>
        ) : null}
        <div className="text-xs text-ink-700 mt-1">
          {showPrize ? (
            <>
              Premie:{' '}
              <span className="font-mono num font-semibold">
                {formatChips(comp.chipValue)} chips
              </span>{' '}
              ·{' '}
            </>
          ) : null}
          {isBtp
            ? 'innsatsen settes hos motstanderen på hullet'
            : 'se Sidespill for å registrere'}
        </div>
      </div>
    </>
  )
  const frame =
    'relative mx-2 rounded-2xl p-3 flex items-center gap-3 overflow-hidden shadow-sm'
  const style = {
    background: brand.bgGradient,
    border: `1px solid ${brand.border}`,
  }
  if (isBtp) {
    return (
      <div className={frame} style={style}>
        {inner}
      </div>
    )
  }
  return (
    <Link
      to={`/s/${playerKey}/sidegames#comp-${comp.id}`}
      className={`${frame} block transition-transform active:scale-[0.99]`}
      style={style}
    >
      {inner}
    </Link>
  )
}

/**
 * Renders one banner per active comp on the current hole — each carries
 * its own brand identity, so a hole with both a BTP and a CLOSEST comp
 * shows two distinct cards stacked.
 */
const HoleCompPrompt = ({
  playerKey,
  comps,
  showChips,
}: {
  playerKey: string
  comps: ReadonlyArray<Comp>
  showChips: boolean
}) => {
  if (!comps.length) return null
  return (
    <div className="mt-3 mb-1 flex flex-col gap-2">
      {comps.map((comp) => (
        <HoleCompBanner
          key={comp.id}
          playerKey={playerKey}
          comp={comp}
          showChips={showChips}
        />
      ))}
    </div>
  )
}

export default HoleCompPrompt
